/**
 * An invoice's audit trail, as the activity feed the invoice detail shows the latest of:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-history.tsx
 *   an append-only event trail, one row per event;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/social-media/dashboard/recent-activities.tsx
 *   the feed rows, through `AuditFeed`.
 *
 * Every state change, nothing filtered out — an audit trail with a filter is one someone reads the
 * wrong subset of. So no search box and no paging: the whole trail is on the page.
 */
import { PiScrollDuotone as AuditIcon } from "react-icons/pi";
import { Badge } from "rizzui";
import { useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { PageHero } from "../../components/detail-parts";
import { AuditFeed } from "../../components/invoicing/parts";
import { getInvoiceAudit, invoiceByKey } from "../../lib/services/invoicing";
import { invoicePath } from "../../lib/paths";

export async function invoiceAuditLoader({ params, request }: LoaderFunctionArgs) {
  const invoice = await invoiceByKey(request, params.invoiceNumber!);
  return { entries: await getInvoiceAudit(invoice.id), href: invoicePath(params.invoiceNumber!) };
}

export default function InvoiceAuditPage() {
  const { entries, href } = useLoaderData() as Awaited<ReturnType<typeof invoiceAuditLoader>>;

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: "Invoice", href }, { name: "Audit Trail" }]}
        back={{ href, label: "Back to the invoice" }}
        icon={<AuditIcon aria-hidden />}
        iconClassName="bg-orange"
        title="Audit Trail"
        description="Every state change recorded against this invoice, newest first. Entries are immutable and none is filtered out."
        meta={
          <Badge variant="flat" color="warning" rounded="pill">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </Badge>
        }
      />
      <AuditFeed entries={entries} title="Every change" />
    </div>
  );
}
